import { Component, OnInit, Inject } from "@angular/core";
import { Router } from "@angular/router";
import { Header, Ruleset } from "@aptsi-types";
import { RulesetService } from "../ruleset.service";
import { MatDialog, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { DeleteDialogComponent } from "../../shared/dialogs/delete-dialog/delete-dialog.component";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { EnvironmentService } from "src/app/core/service/environment.service";
@Component({
  selector: "app-ruleset-search",
  templateUrl: "./ruleset-search.component.html",
  styleUrls: ["./ruleset-search.component.scss"],
})
export class RulesetSearchComponent implements OnInit {
  // ─── Pagination ───────────────────────────────────────────────
  currentPage = 1;
  itemsPerPage = 7;
  totalPages = 0;
  totalPagesNormal = 0;
  totalPagesSearch = 0;

  // ─── Search & Filter ──────────────────────────────────────────
  searchText = "";
  filterByHeader: string = "all";
  isSearch = false;
  searchTextChanged = new Subject<string>();

  // ─── Data ─────────────────────────────────────────────────────
  rulesets: Ruleset[] = [];
  filteredData: Ruleset[] = [];
  searchedData: Ruleset[] = [];

  // ─── UI Configuration ─────────────────────────────────────────
  buttonConfig = {
    update: true,
    delete: true,
    capture: false,
  };

  headers: Header[] = [
    {
      name: "Ruleset Id",
      property: "rulesetid",
      showInDropdown: true,
      description: "Unique identifier for the ruleset",
    },
    {
      name: "Ruleset Name",
      property: "name",
      showInDropdown: true,
      description: "Name of the ruleset",
    },
    {
      name: "Ruleset Type",
      property: "rulesetType",
      showInDropdown: true,
      description: "Type or category of the ruleset",
    },
    {
      name: "Last Updated",
      property: "timestamp",
      showInDropdown: false,
      description: "Timestamp of the last update to the ruleset",
    },
  ];

  constructor(
    private router: Router,
    private rulesetService: RulesetService,
    private dialog: MatDialog,
    public environmentService: EnvironmentService 
  ) {}

  // ──────────────────────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    //     const dropdownHeaders = this.headers.filter(
    //   header => header.showInDropdown
    // );

    // if (dropdownHeaders.length > 1) {
    //   this.filterByHeader = dropdownHeaders[1].property;
    // }
    
    this.fetchRulesets();

    this.searchTextChanged
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchText) => {
        this.searchText = searchText;
        this.searchRulesets();
      });
  }

  // ──────────────────────────────────────────────────────────────
  // Pagination
  // ──────────────────────────────────────────────────────────────

  get paginatedData(): Ruleset[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.isSearch
      ? this.searchedData.slice(start, end)
      : this.filteredData.slice(start, end);
  }

  changePage(page: number): void {
    this.currentPage = page;
    this.totalPages = this.isSearch
      ? this.totalPagesSearch
      : this.totalPagesNormal;
    this.isSearch ? this.fetchRulesetSearch() : this.fetchRulesets();
  }

  // ──────────────────────────────────────────────────────────────
  // Fetching Data
  // ──────────────────────────────────────────────────────────────

  async fetchRulesets(): Promise<void> {
    try {
      const response = await this.rulesetService
        .getRulesets(this.currentPage - 1)
        .toPromise();

      this.filteredData = response.map((r) => ({
        ...r,
        timestamp: this.formatDate(r.timestamp),
      }));

      this.syncPagination(response);
    } catch (error) {
      console.error("Error fetching rulesets:", error);
    }
  }

  async fetchRulesetSearch(): Promise<void> {
    try {
      const response = await this.rulesetService
        .getRulesetSearch(
          this.currentPage - 1,
          this.searchText,
          this.filterByHeader
        )
        .toPromise();

      this.searchedData = response.map((r) => ({
        ...r,
        timestamp: this.formatDate(r.timestamp),
      }));

      this.syncPagination(response);
    } catch (error) {
      console.error("Error fetching search results:", error);
    }
  }

  private syncPagination(response: any[]): void {
    if (response.length > 0 && response[0].totalPages) {
      const totalPages = response[0].totalPages;
      const currentPage = response[0].currentPage + 1;

      if (this.isSearch) {
        this.totalPagesSearch = totalPages;
      } else {
        this.totalPagesNormal = totalPages;
      }

      this.totalPages = totalPages;
      this.currentPage = currentPage;
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Search
  // ──────────────────────────────────────────────────────────────

  // async searchRulesets(): Promise<void> {
  //   try {
  //     if (this.searchText) {
  //       this.isSearch = true;

  //       await this.fetchRulesetSearch();

  //       const selectedHeader = this.headers
  //         .filter((h) => h.showInDropdown)
  //         .find((h) => h.property === this.filterByHeader);

  //       if (selectedHeader) {
  //         this.searchedData = this.searchedData.filter((item) => {
  //           const val = item[selectedHeader.property];
  //           return val
  //             ?.toString()
  //             .toLowerCase()
  //             .includes(this.searchText.toLowerCase());
  //         });

  //         this.searchedData.sort((a, b) =>
  //           a[selectedHeader.property]
  //             ?.toString()
  //             .localeCompare(b[selectedHeader.property]?.toString())
  //         );
  //       } else {
  //         this.filteredData = [];
  //       }
  //     } else {
  //       this.isSearch = false;

  //       await this.fetchRulesets();
  //     }
  //   } catch (error) {
  //     console.error("Error searching rulesets:", error);
  //   }
  // }

  async searchRulesets(): Promise<void> {

    try {

      if (this.searchText?.trim()) {

        this.isSearch = true;
        this.currentPage = 1;

        if (!this.filterByHeader) {
          this.filterByHeader = 'all';
        }

        // Backend handles:
        // search
        // pagination
        // filtering
        await this.fetchRulesetSearch();

      } else {

        this.isSearch = false;
        this.currentPage = 1;

        await this.fetchRulesets();
      }

    } catch (error) {

      console.error(
        'Error during search:',
        error
      );
    }
  }

  onSearchTextChange(searchValue: string): void {
    this.searchTextChanged.next(searchValue);
  }

  // ──────────────────────────────────────────────────────────────
  // Actions
  // ──────────────────────────────────────────────────────────────

  handleAction(event: {
    action: string;
    rowIndex: number;
    rowData: any;
  }): void {
    const index = event.rowIndex;
    if (event.action === "update") {
      this.updateRuleset(index);
    } else if (event.action === "delete") {
      this.deleteRuleset(index);
    }
  }

  async deleteRuleset(index: number): Promise<void> {
    try {
      const dialogRef = this.dialog.open(DeleteDialogComponent, {
        data: {
          title: "Are you sure?",
          message: "Do you really want to delete this Ruleset?",
          isConfirm: false,
        },
      });

      const confirmed = await dialogRef.afterClosed().toPromise();
      if (!confirmed) return;

      const rulesetId = this.isSearch
        ? this.searchedData[index].rulesetid
        : this.filteredData[index].rulesetid;
      await this.rulesetService.deleteRuleset(rulesetId).toPromise();
      await this.fetchRulesets();

      this.dialog.open(DeleteDialogComponent, {
        data: {
          title: "Success",
          message: "The Ruleset has been deleted successfully.",
          isConfirm: true,
        },
      });
    } catch (error) {
      console.error("Error deleting Ruleset:", error);
    }
  }

  updateRuleset(index: number): void {
    const rulesetId = this.isSearch
      ? this.searchedData[index].rulesetid
      : this.filteredData[index].rulesetid;
    this.router.navigate([
      "ada-access/auth-tools/policy-authoring/ruleset/update",
      rulesetId,
    ]);
  }

  createRulesets(): void {
    this.router.navigate([
      "ada-access/auth-tools/policy-authoring/ruleset/create",
    ]);
  }

  back(): void {
    if (this.isSearch) {
      this.isSearch = false;
      this.fetchRulesets();
    } else {
      this.router.navigate(["ada-access/auth-tools/policy-authoring"]);
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Utilities
  // ──────────────────────────────────────────────────────────────

  private formatDate(isoString: string): string {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replace(",", "");
  }
}
